/// <amd-module name="Test/TestPlansLibrary/TestCasesPivotFilter/Component/TestCasesPivotFilterComponent" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";

import { VssComponent, IVssComponentContext, WrappedComponent } from "VSS/Platform/Layout";
import { IIdentity } from "VSSUI/IdentityPicker";

import {
    ITestPlansLibraryFilterComponentProps,
    ITestPlansLibraryFilterBarItemBase,
    ITestPlansLibraryFilterBarItemIdentity
} from "Test/TestPlansLibrary/TestPlansLibraryFilter/TestPlansLibraryFilterContracts";

import { TastCasesPivotFilterActions } from "../Redux/TestCasesPivotFilterActions";
import { ITestCasesPivotFilterAwareState } from "../Redux/TestCasesPivotFilterContracts";
import { getTestCasesPivotFilterModule } from "../Redux/TestCasesPivotFilterModule";
import { getFilterBarItemsFromState, getAssignedToSelectionFromState } from "../Redux/TestCasesPivotFilterSelector";
import * as Constants from "../TestCasesPivotFilterConstants";

export interface ITestCasesPivotFilterComponentProps extends ITestPlansLibraryFilterComponentProps {
    // List of identities currently selected in assigned to filter
    assignedToSelection: IIdentity[];

    // Dispatcher when an identity is added in assigned to filter
    assignedToIdentityAdded: (identity: IIdentity) => void;

    // Dispatcher when an identity is removed from assigned to filter
    assignedToIdentityRemoved: (identity: IIdentity) => void;

    // Dispatcher when multiple identities are removed from assigned to filter
    assignedToIdentitiesRemoved: (identiities: IIdentity[]) => void;
}

/**
 * TestCasesPivotFilterComponent - This component renders the filter bar for test cases pivot with help of TestPlansLibraryFilterComponent.
 * It doesn't have a logic of rendering as such. It maintains the state of filters that are not managed through FilterContext.
 */
export class TestCasesPivotFilterComponent extends VssComponent<ITestCasesPivotFilterComponentProps> {
    constructor(props: ITestCasesPivotFilterComponentProps, context?: IVssComponentContext) {
        super(props, context);
    }

    public render(): JSX.Element {
        // Inject the callbacks for assigned to filter.
        // This needs to be done here, because
        // 1. We need to abstract out the specifics like 'Assigned to' from TestPlansLibraryFilterComponent.
        //    It only understands a filter type like 'Identity' and takes generic callbacks like 'identityAdded'.
        // 2. TestPlansLibraryFilterComponent is not connected to Redux.
        let assignedToItem: ITestPlansLibraryFilterBarItemBase | undefined = this.props.filterBarItems.find(
            item => item.filterItemKey === Constants.TestCasesAssignedToFilterItemKey
        );
        if (assignedToItem) {
            (assignedToItem as ITestPlansLibraryFilterBarItemIdentity).identitySelection = this.props.assignedToSelection;
            (assignedToItem as ITestPlansLibraryFilterBarItemIdentity).identityAdded = this.props.assignedToIdentityAdded;
            (assignedToItem as ITestPlansLibraryFilterBarItemIdentity).identityRemoved = this.props.assignedToIdentityRemoved;
            (assignedToItem as ITestPlansLibraryFilterBarItemIdentity).identitiesRemoved = this.props.assignedToIdentitiesRemoved;
        }

        return (
            <DynamicModuleLoader modules={[getTestCasesPivotFilterModule()]}>
                <WrappedComponent
                    dependencies={["ms.vss-test-web.testplans-library-filter-component"]}
                    wrappedType={"testplans-library-filter-component"}
                    filterBarItems={this.props.filterBarItems}
                    className={"test-cases-pivot-filter-bar bolt-filterbar-neutral depth-8"}
                />
            </DynamicModuleLoader>
        );
    }
}

function mapStateToProps(state: ITestCasesPivotFilterAwareState): Partial<ITestCasesPivotFilterComponentProps> {
    const filterBarItems = getFilterBarItemsFromState(state);
    const assignedToSelection = getAssignedToSelectionFromState(state);

    return {
        filterBarItems,
        assignedToSelection: assignedToSelection
    };
}

function mapDispatchToProps(dispatch: Dispatch): Partial<ITestCasesPivotFilterComponentProps> {
    return {
        assignedToIdentityAdded: (identity: IIdentity) => dispatch(TastCasesPivotFilterActions.addAssignedToIdentity(identity)),
        assignedToIdentityRemoved: (identity: IIdentity) => dispatch(TastCasesPivotFilterActions.removeAssignedToIdentity(identity)),
        assignedToIdentitiesRemoved: (identities: IIdentity[]) => dispatch(TastCasesPivotFilterActions.removeAssignedToIdentities(identities))
    };
}

export const TestCasesPivotFilterConnectedComponent = connect(
    mapStateToProps,
    mapDispatchToProps
)(TestCasesPivotFilterComponent);

VssComponent.register("testcases-pivot-filter-component", TestCasesPivotFilterConnectedComponent as ((props: ITestCasesPivotFilterComponentProps) => JSX.Element));
